/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-02-07 20:34:55
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import isNil from "lodash-es/isNil";
import Utility from "@/utilities/Utility.ts";
import JsonUtils from "@/utilities/JsonUtils.ts";

class Environments {
	#react_prefix: string = "VITE_";
	#values: Record<string, any> = {};

	/**
	 * Retrieves the values stored in the current instance.
	 *
	 * @return {Record<string, any>} The values stored in a key-value pair format.
	 */
	public get values(): Record<string, any> {
		return this.#values;
	}

	/**
	 * Sets the values for this instance.
	 *
	 * @param {Record<string, any>} values - An object containing key-value pairs to assign to the instance.
	 */
	public set values(values: Record<string, any>) {
		this.#values = values;
	}

	/**
	 * Retrieves an environment variable by key. If the variable is not set, returns a default value.
	 *
	 * @param {string} key - The key of the environment variable to retrieve.
	 * @param {T} defValue - The default value to return if the environment variable is not set.
	 * @return {T} The value of the environment variable if it exists, otherwise the default value.
	 */
	#env_by_value<T>(key: string, defValue: T): T {
		const reactAppResult = this.values[`${this.#react_prefix}${key}`];
		const result = this.values[key];
		if (!isNil(reactAppResult)) {
			return JsonUtils.to_object<T>(reactAppResult) as T;
		}
		if (!isNil(this.values[key])) {
			return JsonUtils.to_object<T>(result) as T;
		}
		return defValue as T;
	}

	/**
	 * Retrieves an environment variable's value based on the provided key.
	 * If the environment variable does not exist, it returns the provided default value.
	 * If a value exists for the key in the defined environment, it overrides the default value.
	 *
	 * @param {string} key - The key of the environment variable to retrieve.
	 * @param {any} [defValue=null] - The default value to return if the environment variable is not found.
	 * @return {T} - The value of the environment variable, or the default value if the variable is not found.
	 */
	public env<T>(key: string, defValue: any = null): T {
		const result = isNil(this.#env_by_value(key, defValue))
			? process.env[`${this.#react_prefix}${key}`]
			: this.#env_by_value<T>(key, defValue);
		return result as T;
	}

	/**
	 * Indicates whether the current environment is in development mode.
	 *
	 * @return {boolean} Returns true if the environment is in development mode, false otherwise.
	 */
	public get is_developement() {
		return this.values.DEV;
	}

	/**
	 * Determines if the current environment is a production environment.
	 *
	 * @return {boolean} Returns true if the environment is production, otherwise false.
	 */
	public get is_production() {
		return this.values.PROD;
	}

	/**
	 * Retrieves the current product mode setting from the environment configuration.
	 *
	 * @return {string} The product mode setting.
	 */
	public get productMode(): string {
		return this.env<string>("PRODUCT_MODE");
	}

	/**
	 * Retrieves the normalized web domain from the environment variables.
	 * @return {string} The normalized web domain.
	 */
	public get webDomain(): string {
		const domain = this.env<string | null>("WEB_DOMAIN");
		return Utility.url_normalize(domain);
	}

	/**
	 * Retrieves and normalizes the API domain from the environment variables.
	 *
	 * @return {string} The normalized API domain as a string.
	 */
	public get apiDomain(): string {
		const domain = this.env<string | null>("API_DOMAIN");
		return Utility.url_normalize(domain);
	}

	/**
	 * Retrieves and normalizes the WebSocket URI from the environment variables.
	 *
	 * @return {URL} The normalized WebSocket URI.
	 */
	public get stompDomain(): URL {
		const domain = this.env<string | null>("WEBSOCKET_URI");
		return new URL(Utility.url_normalize(domain));
	}
}
export default new Environments();
